import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { forwardRef, useContext, useEffect, useRef, useState } from 'react';
import CustomSelect2 from './CustomSelect2';
import { Box, Typography, FormControlLabel, Checkbox, Grid, TextField, InputAdornment, Select, MenuItem, colors, Button, Skeleton, Dialog, DialogTitle, DialogContent, DialogActions, Chip, Snackbar } from '@mui/material';
import SelectPlanCheckbox from './SelectPlanCheckbox';
import { ConfigContext } from '../utils/ConfigContext';
import TableRatesRow from './TableRatesRow';
import getSafe from '../utils/getSafe';
import domtoimage from 'dom-to-image';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import PercentInput from './PercentInput';
import NotesDisplay from './NotesDisplay';
import RatesVersionSelect from './RatesVersionSelect';
import CopyImageButton from './CopyImageButton';
import CopyExcelDataButton from './CopyExcelDataButton';

const IHHP_Rates = forwardRef(({ appConfig, sx, clients, loadingStatus, currency, selectedPlans, setSelectedPlans,
  response, deductibles, setDeductibles, addToPendingTasks,
  discounts, setDiscounts, computedRates, setComputedRates,
  areaOfCoverage, specificAreaOfCoverage, setSpecificAreaOfCoverage,
  setShowImageCopy, selectedOptions, addSelectedOption,
  setViewSelectedOption, specificCurrency, setSpecificCurrency,
  specificPaymentFrequency, setSpecificPaymentFrequency, additionalLoadings, setAdditionalLoadings , insurerRates = [], selectedRatesVersion, setSelectedRatesVersion, handleFetchAppConfig, setShowRefreshNow }, ref) => {
  // const { appConfig } = useContext(ConfigContext);

  const id = 'ihhp';

  const plans = ['ihhp_hospital_plan', 'ihhp_non_hospitalisation_benefits', 'ihhp_medicine_appliances', 'ihhp_medical_evacuation_repatriation', 'ihhp_dental_optical_a', 'ihhp_dental_optical_b'];

  const loadings = getSafe(() => appConfig.content.specifics.ihhp.loadings);
  const specific_area_of_coverages = getSafe(() => appConfig.content.ihhp_area_of_coverage);

  const currencies = ["USD", "EURO", "GBP"];

  // Module key -> plan id / computed rate field / short label, in display order
  const modules = [
    { key: 'hp', planId: 'ihhp_hospital_plan', rateKey: 'hospital_plan', label: 'HP', name: 'Hospital Plan' },
    { key: 'm1', planId: 'ihhp_non_hospitalisation_benefits', rateKey: 'non_hospitalisation_benefits', label: 'M1', name: 'Module 1 (Non-Hospitalisation Benefits)' },
    { key: 'm2', planId: 'ihhp_medicine_appliances', rateKey: 'medicine_appliances', label: 'M2', name: 'Module 2 (Medicine & Appliances)' },
    { key: 'm3', planId: 'ihhp_medical_evacuation_repatriation', rateKey: 'medical_evacuation_repatriation', label: 'M3', name: 'Module 3 (Medical Evacuation)' },
    { key: 'm4a', planId: 'ihhp_dental_optical_a', rateKey: 'dental_optical_a', label: 'M4A', name: 'Module 4A (Dental & Optical)' },
    { key: 'm4b', planId: 'ihhp_dental_optical_b', rateKey: 'dental_optical_b', label: 'M4B', name: 'Module 4B (Enhanced Dental & Optical)' },
  ];

  const emptyPlanSelection = { hp: false, m1: false, m2: false, m3: false, m4a: false, m4b: false };

  const [comparisonDialogOpen, setComparisonDialogOpen] = useState(false);
  const [comparisonSelection, setComparisonSelection] = useState(emptyPlanSelection);
  const [comparisons, setComparisons] = useState([]);
  const comparisonIdCounter = useRef(0);

  // While comparing, the plan checkboxes are driven by the comparisons and locked
  const isComparing = comparisons.length > 0;
  const [showComparingLockedSnackbar, setShowComparingLockedSnackbar] = useState(false);
  const onComparingLockedChange = isComparing ? () => setShowComparingLockedSnackbar(true) : null;
  const comparingCheckboxSx = isComparing ? { color: '#98884a', '&.Mui-checked': { color: '#98884a' } } : undefined;

  useEffect(() => {
    computedRates[id] = getComputedRates();
    setComputedRates(computedRates);
  }, [response, discounts, additionalLoadings]);

  // While comparing, the plan checkboxes and table rows reflect only the modules used by the comparisons
  useEffect(() => {
    if (comparisons.length === 0) { return; }

    const used = { ...emptyPlanSelection };

    comparisons.forEach((comparison) => {
      Object.keys(used).forEach((plan) => {
        used[plan] = used[plan] || !!comparison[plan];
      });
    });

    applyPlanSelection(used);
  }, [comparisons]);

  // Checks exactly the given modules (hp, m1, m2, m3, m4a, m4b) in the plan checkboxes
  function applyPlanSelection(selection) {
    const nextPlans = [
      ...selectedPlans.filter((plan) => !plans.includes(plan)),
      ...modules.filter((module) => selection[module.key]).map((module) => module.planId),
    ];

    const isSamePlans = nextPlans.length === selectedPlans.length && nextPlans.every((plan) => selectedPlans.includes(plan));

    if (!isSamePlans) {
      setSelectedPlans(nextPlans);
    }
  }

  useEffect(() => {
    setTimeout(() => {
      addSelectedOption(id, 'specific_area_of_coverage', specificAreaOfCoverage[id]);
      addSelectedOption(id, 'specific_currency', specificCurrency[id]);
      addSelectedOption(id, 'specific_payment_frequency', specificPaymentFrequency[id]);
      addSelectedOption(id, 'deductible', deductibles[id]);
      addSelectedOption(id, 'discount', discounts[id] == '' ? 0 : discounts[id]);
      addSelectedOption(id, 'loading', additionalLoadings[id] == '' ? 0 : additionalLoadings[id]);
      addSelectedOption(id, 'comparisons', JSON.stringify(comparisons));
    }, 1000);
  }, [specificAreaOfCoverage[id], specificCurrency[id], specificPaymentFrequency[id], deductibles[id], discounts[id], additionalLoadings[id], comparisons]);

  useEffect(() => {
    setViewSelectedOption(id, 'deductible', (option_value) => {
      deductibles[id] = option_value;
      setDeductibles(deductibles);
    });
    setViewSelectedOption(id, 'discount', (option_value) => {
      discounts[id] = option_value;
      setDiscounts(discounts);
    });

    setViewSelectedOption(id, 'loading', (option_value) => {
      additionalLoadings[id] = option_value;
      setAdditionalLoadings(additionalLoadings);
    });

    setViewSelectedOption(id, 'specific_area_of_coverage', (option_value) => {
      specificAreaOfCoverage[id] = option_value;
      setSpecificAreaOfCoverage(specificAreaOfCoverage);
    });

    setViewSelectedOption(id, 'specific_currency', (option_value) => {
      specificCurrency[id] = option_value;
      setSpecificCurrency(specificCurrency);
    });

    setViewSelectedOption(id, 'specific_payment_frequency', (option_value) => {
      specificPaymentFrequency[id] = option_value;
      setSpecificPaymentFrequency(specificPaymentFrequency);
    });

    setViewSelectedOption(id, 'comparisons', (option_value) => {
      let parsed = option_value;
      if (typeof parsed === 'string') {
        try {
          parsed = JSON.parse(parsed);
        } catch (e) {
          parsed = [];
        }
      }
      parsed = Array.isArray(parsed) && parsed.length > 1 ? parsed : [];

      setComparisons(parsed);

      const maxId = parsed.reduce((max, comparison) => Math.max(max, comparison.id || 0), 0);
      comparisonIdCounter.current = Math.max(comparisonIdCounter.current, maxId);
    });
  }, [selectedOptions]);

  function getComputedRates() {
    if (!response) return;

    const rates = response.rates;
    const discount = parseFloat(discounts[id]) || 0;
    const loading = parseFloat(additionalLoadings[id]) || 0;
    const totalPercent = ((100 - discount + loading) * 0.01);

    const computedRates = [];

    for (let i = 0; i < rates.length; i++) {
      const hospital_plan = computedRate(rates[i].hospital_plan, rates[i].client.age);
      const non_hospitalisation_benefits = computedRate(rates[i].non_hospitalisation_benefits, rates[i].client.age);
      const medicine_appliances = computedRate(rates[i].medicine_appliances, rates[i].client.age);
      const medical_evacuation_repatriation = computedRate(rates[i].medical_evacuation_repatriation, rates[i].client.age);
      const dental_optical_a = computedRate(rates[i].dental_optical_a, rates[i].client.age);
      const dental_optical_b = computedRate(rates[i].dental_optical_b, rates[i].client.age);

      computedRates.push({
        'client': rates[i].client,
        'currency': rates[i].currency,
        'hospital_plan': hospital_plan * totalPercent,
        'non_hospitalisation_benefits': non_hospitalisation_benefits * totalPercent,
        'medicine_appliances': medicine_appliances * totalPercent,
        'medical_evacuation_repatriation': medical_evacuation_repatriation * totalPercent,
        'dental_optical_a': dental_optical_a * totalPercent,
        'dental_optical_b': dental_optical_b * totalPercent,
      })
    }

    return computedRates;
  }


  function checkIfThereIsAnySelectedPlan() {
    for (let i = 0; i < selectedPlans.length; i++) {
      if (plans.includes(selectedPlans[i])) {
        return true;
      }
    }

    return false;
  }

  function computeIHHP_Rates(rates) {
    const computedRates = [];

    for (let i = 0; i < rates.length; i++) {
      computedRates.push({
        'client': rates[i].client,
        'currency': rates[i].currency,
        'hospital_plan': computedRate(rates[i].hospital_plan, rates[i].client.age),
        'non_hospitalisation_benefits': computedRate(rates[i].non_hospitalisation_benefits, rates[i].client.age),
        'medicine_appliances': computedRate(rates[i].medicine_appliances, rates[i].client.age),
        'medical_evacuation_repatriation': computedRate(rates[i].medical_evacuation_repatriation),
        'dental_optical_a': computedRate(rates[i].dental_optical_a),
        'dental_optical_b': computedRate(rates[i].dental_optical_b)
      })
    }

    return computedRates;
  }

  function computedRateWithLoadings(rate, age) {
    if (rate === 'Unavailable' || typeof rate === 'string') {
      return rate;
    }

    const loadingsVal = getLoadings(age);

    return rate + (rate * loadingsVal);
  }

  function computedRate(rate) {
    if (rate === 'Unavailable' || typeof rate === 'string') {
      return rate;
    }

    return rate;
  }

  function onInputDiscountChange(e) {
    let value = e.target.value;

    // Allow only numbers and at most one decimal point
    value = value.replace(/[^0-9.]/g, '');

    // Prevent multiple decimals
    if ((value.match(/\./g) || []).length > 1) {
      value = value.substring(0, value.lastIndexOf('.'));
    }

    // Ensure only two decimal places
    if (value.includes('.')) {
      const [integerPart, decimalPart] = value.split('.');
      value = `${integerPart}.${decimalPart.substring(0, 2)}`;
    }

    // Remove leading zeros (but allow "0" or "0.x" scenarios)
    if (value !== '' && value !== '0' && value.startsWith('0') && !value.startsWith('0.')) {
      value = value.replace(/^0+/, '');
    }

    // Limit value to a maximum of 100
    if (parseFloat(value) > 100) {
      value = '100';
    }

    discounts[id] = value;

    setDiscounts({ ...discounts });
  }

  function getEquivalentAreaOfCoverage() {
    let _areaOfCoverage = areaOfCoverage;

    if (specificAreaOfCoverage[id] === 'parent') {
      _areaOfCoverage = areaOfCoverage;
    } else {
      _areaOfCoverage = specificAreaOfCoverage[id];
    }

    if (_areaOfCoverage === 'Worldwide' || _areaOfCoverage === 'Worldwide Plus' || _areaOfCoverage === 'HONG KONG & U.S') {
      return 'HONG KONG & U.S';
    }

    if (_areaOfCoverage === 'EUROPEAN ECONOMIC AREA') {
      return 'EUROPEAN ECONOMIC AREA';
    }

    return 'REST OF THE WORLD (Excl. Hong Kong & U.S. & European Economic Area)';
  }

  function getLoadings(age) {
    for (let range in loadings) {
      let [min, max] = range.split('-').map(Number);
      if (age >= min && age <= max) {
        return loadings[range];
      }
    }
    return 0;
  }

  function displayLoadings(age) {
    const val = getLoadings(age);

    if (val === 0) return '';

    return '(' + (val * 100) + '% loadings)'
  }

  function titleCasePhrase(str, phraseToTitleCase) {
    const regex = new RegExp(phraseToTitleCase, 'gi');
    return str.replace(regex, match => {
      return match
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    });
  }

  function getLivePlanSelection() {
    const selection = {};

    modules.forEach((module) => {
      selection[module.key] = selectedPlans.includes(module.planId);
    });

    return selection;
  }

  function buildPlanLabel(selection) {
    return modules.filter((module) => selection[module.key]).map((module) => module.label).join(' + ');
  }

  function getPlanLabel() {
    return buildPlanLabel(getLivePlanSelection());
  }

  function openComparisonDialog() {
    setComparisonSelection({ ...emptyPlanSelection, hp: true });
    setComparisonDialogOpen(true);
  }

  function toggleComparisonSelection(field) {
    // Hospital Plan is compulsory
    if (field === 'hp') { return; }

    setComparisonSelection((prev) => {
      const next = { ...prev, [field]: !prev[field] };

      // Module 4A and 4B are mutually exclusive
      if (field === 'm4a' && next.m4a) { next.m4b = false; }
      if (field === 'm4b' && next.m4b) { next.m4a = false; }

      return next;
    });
  }

  function nextComparisonId() {
    comparisonIdCounter.current += 1;
    return comparisonIdCounter.current;
  }

  function isSamePlanSelection(a, b) {
    return modules.every((module) => !!a[module.key] === !!b[module.key]);
  }

  function addComparison() {
    const existing = comparisons.length === 0 ? [getLivePlanSelection()] : comparisons;
    const isDuplicate = existing.some((comparison) => isSamePlanSelection(comparison, comparisonSelection));

    if (isDuplicate) {
      setComparisonDialogOpen(false);
      return;
    }

    setComparisons((prev) => {
      const next = [...prev];

      if (next.length === 0) {
        next.push({ id: nextComparisonId(), ...getLivePlanSelection() });
      }

      next.push({ id: nextComparisonId(), ...comparisonSelection });

      return next;
    });
    setComparisonDialogOpen(false);
  }

  function removeComparison(comparisonId) {
    const next = comparisons.filter((comparison) => comparison.id !== comparisonId);

    // A single comparison is no comparison, revert to the default dynamic total row
    // with the remaining combination checked
    if (next.length === 1) {
      applyPlanSelection(next[0]);
    }

    setComparisons(next.length <= 1 ? [] : next);
  }

  function getPlanSortRank(selection) {
    let rank = 0;

    modules.forEach((module, index) => {
      if (selection[module.key]) {
        rank += Math.pow(2, index);
      }
    });

    return rank;
  }

  function getSortedComparisons() {
    return [...comparisons].sort((a, b) => getPlanSortRank(a) - getPlanSortRank(b));
  }

  function formatRate(rate) {
    if (isNaN(rate) || rate == 'Unavailable' || typeof rate === 'string') {
      return <span style={{ color: '#f44336' }}>{'Unavailable'}</span>
    }

    if (isNaN(rate)) {
      return rate;
    }

    return getCurrencySign() + rate.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function getCurrencySign() {
    const currency = getCurrency();

    if (currency === 'USD') {
      return '$';
    } else if (currency === 'EURO') {
      return '€';
    } else if (currency === 'GBP') {
      return '£';
    } else if (currency === 'SGD') {
      return 'S$';
    }

    return '';
  }

  function getCurrency() {
    const computedRates = getComputedRates();

    if (!computedRates) return '';

    if (computedRates.length === 0) { return '' }

    return computedRates[0].currency;
  }

  function computeTotalPremiumForPlansPerClient(selection, clientIndex) {
    const computedRates = getComputedRates();

    if (!computedRates) return 0;

    const rates = computedRates[clientIndex];

    if (!rates) return 0;

    let total = 0;

    // Modules are only added on top of the Hospital Plan
    if (selection.hp) {
      modules.forEach((module) => {
        if (selection[module.key] && !isNaN(rates[module.rateKey])) {
          total += rates[module.rateKey];
        }
      });
    }

    return total;
  }

  function computeTotalPremiumForPlansPerPlan(selection) {
    let total = 0;

    for (let i = 0; i < clients.length; i++) {
      const totalPerClient = computeTotalPremiumForPlansPerClient(selection, i);
      if (!isNaN(totalPerClient)) {
        total += totalPerClient;
      }
    }

    return total;
  }

  function computeTotalPremiumPerPlan() {
    return computeTotalPremiumForPlansPerPlan(getLivePlanSelection());
  }

  function computeTotalPremiumPerClient(clientIndex) {
    return computeTotalPremiumForPlansPerClient(getLivePlanSelection(), clientIndex);
  }

  return (
    <Box id={id} sx={sx}>
      <Paper sx={{ borderColor: '#e0e0e0', borderWidth: 2, pb: 2 }} variant='outlined' >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#b3a66e', px: 2, py: 1 }}>
          <div style={{
            color: '#fff',
            backgroundColor: '#98884a',
            width: 100,
            height: 60,
            padding: 6,
            fontFamily: 'Poppins, sans-serif',
            fontSize: 13,
            lineHeight: 1.2,
          }}>International Health and Hospital plan</div>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <img src={`${process.env.PUBLIC_URL}/images/KSK Logo.png`} alt="KSK Insurance" style={{ height: 60, userSelect: 'none', WebkitUserDrag: 'none' }} draggable={false} onContextMenu={(e) => e.preventDefault()} />
            <img src={`${process.env.PUBLIC_URL}/images/Bupa Yellow Logo.png`} alt="BUPA" style={{ height: 60, userSelect: 'none', WebkitUserDrag: 'none' }} draggable={false} onContextMenu={(e) => e.preventDefault()} />
          </Box>
        </Box>

        <Box sx={{ p: 2 }}>
          {/* <Grid container alignItems='center'>
            <Grid item xs={1.5}>
              <Typography fontSize={18} fontWeight={600}>Select Plans</Typography>
            </Grid>
            <Grid item xs={1.3}>
              <FormControlLabel control={<SelectPlanCheckbox selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId='ihhp_hospital_plan' />} label="Hospital Plan" />
            </Grid>
            <Grid item xs={1.3}>
              <FormControlLabel control={<SelectPlanCheckbox selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId='ihhp_non_hospitalisation_benefits' />} label="Module 1" />
            </Grid>
            <Grid item xs={1.3}>
              <FormControlLabel control={<SelectPlanCheckbox selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId='ihhp_medicine_appliances' />} label="Module 2" />
            </Grid>
            <Grid item xs={1.3}>
              <FormControlLabel control={<SelectPlanCheckbox selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId='ihhp_medical_evacuation_repatriation' />} label="Module 3" />
            </Grid>

            <Grid item xs={1.3}>
              <FormControlLabel control={<SelectPlanCheckbox selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId='ihhp_dental_optical_a' />} label="Module 4A" />
            </Grid>
            <Grid item xs={1.3}>
              <FormControlLabel control={<SelectPlanCheckbox selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId='ihhp_dental_optical_b' />} label="Module 4B" />
            </Grid>
            <Grid item xs={12}>
            </Grid>
            <Grid item xs={1.5}>
              <Typography fontSize={18} fontWeight={600}>Deductible</Typography>
            </Grid>
            <Grid item xs={1.8}>
              <CustomSelect2
                value={deductibles[id]}
                onChange={(e) => {
                  // setDeductible(e.target.value);

                  // setDeductibleIndex(getDeductibleIndex(e.target.value));

                  const val = e.target.value;

                  const _deductibles = JSON.parse(JSON.stringify(deductibles));

                  _deductibles[id] = val;

                  setDeductibles(_deductibles);

                  addToPendingTasks([id]);

                }}
                items={getSafe(() => appConfig.content.deductibles.ihhp[currency], [])}
                disabled={loadingStatus[id]}
              />
            </Grid>


            <Grid item xs={12}></Grid>
            <Grid item xs={1.5} mt={1}><Typography fontSize={18} fontWeight={600}>Renewal/Further Discount</Typography></Grid>
            <Grid item xs={1} mt={1}>
              <TextField
                size='small'
                sx={{ width: '80px', textAlign: 'end' }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                value={discounts[id]}
                onChange={onInputDiscountChange}
              />
            </Grid>
            <Grid item xs={1.5}><Typography fontSize={18} fontWeight={600}>Area Of Coverage</Typography></Grid>
            <Grid item xs={2}>
              <Select
                size='small'
                value={specificAreaOfCoverage[id]}
                onChange={(e) => {
                  specificAreaOfCoverage[id] = e.target.value;
                  setSpecificAreaOfCoverage({ ...specificAreaOfCoverage });

                  addToPendingTasks([id]);
                }}
                sx={{ fontSize: 12, height: 40, width: '190px' }}
                fullWidth
                disabled={loadingStatus[id]}
              >
                <MenuItem value={'parent'} sx={{ color: colors.blue[500] }}>{areaOfCoverage}</MenuItem>
                {
                  specific_area_of_coverages && specific_area_of_coverages.map(area_of_coverage => (
                    <MenuItem sx={{ display: areaOfCoverage === area_of_coverage ? 'none' : 'block' }} key={area_of_coverage} value={area_of_coverage}>{area_of_coverage}</MenuItem>
                  ))
                }

              </Select>
            </Grid>
            <Grid item xs={7} mt={1}>
              <Typography fontSize={18}>
                Area of Cover: <span display='inline-block' style={{ fontSize: 18, color: '#002047', fontWeight: 600 }}>{getEquivalentAreaOfCoverage()}</span>
              </Typography>
            </Grid>
          </Grid> */}

          <Box sx={{ display: 'flex', gap: 5 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography fontSize={15} fontWeight={600}>Plan Type</Typography>
              <FormControlLabel sx={{ mb: 0, '& .MuiFormControlLabel-label': { fontSize: 13 } }} control={<SelectPlanCheckbox size="small" readOnly={isComparing} sx={comparingCheckboxSx} onReadOnlyChange={onComparingLockedChange} selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId='ihhp_hospital_plan' />} label="Hospital Plan" />
              <FormControlLabel sx={{ mb: 0, '& .MuiFormControlLabel-label': { fontSize: 13 } }} control={<SelectPlanCheckbox size="small" readOnly={isComparing} sx={comparingCheckboxSx} onReadOnlyChange={onComparingLockedChange} selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId='ihhp_non_hospitalisation_benefits' />} label="Module 1 (Non-Hospitalisation Benefits)" />
              <FormControlLabel sx={{ mb: 0, '& .MuiFormControlLabel-label': { fontSize: 13 } }} control={<SelectPlanCheckbox size="small" readOnly={isComparing} sx={comparingCheckboxSx} onReadOnlyChange={onComparingLockedChange} selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId='ihhp_medicine_appliances' />} label="Module 2 (Medicine & Appliances)" />
              <FormControlLabel sx={{ mb: 0, '& .MuiFormControlLabel-label': { fontSize: 13 } }} control={<SelectPlanCheckbox size="small" readOnly={isComparing} sx={comparingCheckboxSx} onReadOnlyChange={onComparingLockedChange} selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId='ihhp_medical_evacuation_repatriation' />} label="Module 3 (Medical Evacuation)" />
              <FormControlLabel sx={{ mb: 0, '& .MuiFormControlLabel-label': { fontSize: 13 } }} control={<SelectPlanCheckbox size="small" readOnly={isComparing} sx={comparingCheckboxSx} onReadOnlyChange={onComparingLockedChange} selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId='ihhp_dental_optical_a' onChangeCb={() => {
                if (selectedPlans.includes('ihhp_dental_optical_a')) {
                  const idx = selectedPlans.indexOf('ihhp_dental_optical_b');
                  if (idx !== -1) {
                    selectedPlans.splice(idx, 1);
                    setSelectedPlans([...selectedPlans]);
                  }
                }
              }} />} label="Module 4A (Dental & Optical)" />
              <FormControlLabel sx={{ mb: 0, '& .MuiFormControlLabel-label': { fontSize: 13 } }} control={<SelectPlanCheckbox size="small" readOnly={isComparing} sx={comparingCheckboxSx} onReadOnlyChange={onComparingLockedChange} selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId='ihhp_dental_optical_b' onChangeCb={() => {
                if (selectedPlans.includes('ihhp_dental_optical_b')) {
                  const idx = selectedPlans.indexOf('ihhp_dental_optical_a');
                  if (idx !== -1) {
                    selectedPlans.splice(idx, 1);
                    setSelectedPlans([...selectedPlans]);
                  }
                }
              }} />} label="Module 4B (Enhanced Dental & Optical)" />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, maxWidth: 400 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography fontSize={13} fontWeight={600} sx={{ width: 170 }}>Deductible Option</Typography>
                <CustomSelect2
                  dense
                  sx={{ minWidth: 170 }}
                  value={deductibles[id]}
                  onChange={(e) => {
                    // setDeductible(e.target.value);

                    // setDeductibleIndex(getDeductibleIndex(e.target.value));

                    const val = e.target.value;

                    const _deductibles = JSON.parse(JSON.stringify(deductibles));

                    _deductibles[id] = val;

                    setDeductibles(_deductibles);

                    addToPendingTasks([id]);

                  }}
                  items={getSafe(() => appConfig.content.deductibles.ihhp[specificCurrency[id]], [])}
                  disabled={loadingStatus[id]}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography fontSize={13} fontWeight={600} sx={{ width: 170 }}>Area Of Coverage</Typography>
                <Select
                  size='small'
                  value={specificAreaOfCoverage[id]}
                  onChange={(e) => {
                    specificAreaOfCoverage[id] = e.target.value;
                    setSpecificAreaOfCoverage({ ...specificAreaOfCoverage });

                    addToPendingTasks([id]);
                  }}
                  sx={{ fontSize: 12, height: 32, width: '170px', minWidth: 170 }}
                  fullWidth
                  disabled={loadingStatus[id]}
                >
                  {/* <MenuItem value={'parent'} sx={{ color: colors.blue[500] }}>{areaOfCoverage}</MenuItem> */}
                  {
                    specific_area_of_coverages && specific_area_of_coverages.map(area_of_coverage => (
                      <MenuItem key={area_of_coverage} value={area_of_coverage} sx={{ fontSize: 12 }}>{area_of_coverage}</MenuItem>
                    ))
                  }

                </Select>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography fontSize={13} fontWeight={600} sx={{ width: 170 }}>Currency</Typography>
                <Select
                  size='small'
                  value={specificCurrency[id]}
                  onChange={(e) => {
                    specificCurrency[id] = e.target.value;
                    setSpecificCurrency({ ...specificCurrency });

                    setDeductibles(prev => {
                      return {
                        ...prev,
                        [id]: getSafe(() => appConfig.content.deductibles.ihhp[specificCurrency[id]][0]),
                      };
                    });


                    addToPendingTasks([id]);
                  }}
                  sx={{ fontSize: 12, height: 32, width: '170px', minWidth: 170 }}
                  fullWidth
                  disabled={loadingStatus[id]}
                >
                  {
                    currencies.map(currency => (
                      <MenuItem key={currency} value={currency} sx={{ fontSize: 12 }}>{currency}</MenuItem>
                    ))
                  }

                </Select>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography fontSize={13} fontWeight={600} sx={{ width: 170 }}>Payment Frequency</Typography>
                <Select
                  size='small'
                  value={specificPaymentFrequency[id]}
                  onChange={(e) => {
                    specificPaymentFrequency[id] = e.target.value;
                    setSpecificPaymentFrequency({ ...specificPaymentFrequency });

                    addToPendingTasks([id]);
                  }}
                  sx={{ fontSize: 12, height: 32, width: '170px', minWidth: 170 }}
                  fullWidth
                  disabled={loadingStatus[id]}
                >
                  {
                    ["Annually", "Semi Annually", "Quarterly", "Monthly"].map(payment_frequency => (
                      <MenuItem key={payment_frequency} value={payment_frequency} sx={{ fontSize: 12 }}>{payment_frequency}</MenuItem>
                    ))
                  }

                </Select>
              </Box>
              {/* <Typography fontSize={18}>
                Area of Cover: <span display='inline-block' style={{ fontSize: 18, color: '#002047', fontWeight: 600 }}>{titleCasePhrase(getEquivalentAreaOfCoverage(), "REST OF THE WORLD")}</span>
              </Typography> */}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography fontSize={13} fontWeight={600} sx={{ width: 170 }}>Renewal/Further Discount</Typography>
                <PercentInput
                  value={discounts[id]}
                  onCommit={(value) => setDiscounts(prev => ({ ...prev, [id]: value }))}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography fontSize={13} fontWeight={600} sx={{ width: 170 }}>Renewal/Further Loading</Typography>
                <PercentInput
                  value={additionalLoadings[id]}
                  onCommit={(value) => setAdditionalLoadings(prev => ({ ...prev, [id]: value }))}
                />
              </Box>
            </Box>
          </Box>



          {/* <div style={{ display: 'flex', justifyContent: 'end', width: '100%' }}>
            <Button
              size='small'
              sx={{ mb: 1 }}
              onClick={() => {
                var node = document.getElementById('table-ihhp-rates');

                node.style = 'width: auto;';

                domtoimage.toBlob(node).then(function (blob) {
                  // Create ClipboardItem with blob and its type, and add to an array
                  const data = [new ClipboardItem({ [blob.type]: blob })];
                  // Write the data to the clipboard
                  navigator.clipboard.write(data);

                  setShowImageCopy(true);

                  node.style = 'width: 100%;';
                })
                  .catch(function (error) {
                    console.error('oops, something went wrong!', error);
                    node.style = 'width: 100%;';
                  });
              }}
              endIcon={<ContentCopyIcon />}
            >Copy Image</Button>
          </div> */}
          <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'end', width: '100%', marginBottom: 7 }}>
            <CopyExcelDataButton
              elementId='table-ihhp-rates'
              style={{ marginRight: 7, fontSize: 11, padding: '2px 8px', minHeight: 0 }}
              sx={{ '& .MuiSvgIcon-root': { fontSize: 14 } }}
            />
            <CopyImageButton
              elementId='table-ihhp-rates'
              setShowImageCopy={setShowImageCopy}
              style={{ fontSize: 11, padding: '2px 8px', minHeight: 0 }}
              sx={{ '& .MuiSvgIcon-root': { fontSize: 14 } }}
            />
          </div>

          <TableContainer component={Paper} sx={{ overflowX: 'auto', mb: 2.5 }}>
            <Table id='table-ihhp-rates' size='small' sx={{ minWidth: 650, background: '#fff', width: `calc(100% + ${clients.length > 5 ? clients.length - 5 : 0} * 75px)` }}>
              <TableHead>
                <TableRow style={{ background: '#b3a66e', color: '#fff' }}>
                  <TableCell style={{ width: 400, color: '#fff', fontWeight: 600 }}>Plan</TableCell>
                  {
                    clients.map((client, idx) => {
                      if (client.full_name.trim() === '') {
                        return <TableCell style={{ width: 200, fontWeight: 600, color: '#fff' }} key={idx}>_</TableCell>
                      }

                      return <TableCell style={{ width: 200, color: '#fff', fontWeight: 600 }} key={idx}><div>{client.full_name} ({client.age})</div><div>{client.country_of_residence}</div><div>{displayLoadings(client['age'])}</div></TableCell>
                    })
                  }
                  <TableCell style={{ color: '#fff', fontWeight: 600 }}>Subtotal</TableCell>
                </TableRow>

              </TableHead>
              <TableBody>
                <TableRatesRow
                  planId='ihhp_hospital_plan'
                  planName='hospital_plan'
                  planLabel={<span><span style={{ fontSize: 14 }}>Hospital Plan</span></span>}
                  clients={clients}
                  selectedPlans={selectedPlans}
                  loading={loadingStatus[id]}
                  rates={response ? computeIHHP_Rates(response.rates) : []}
                  currency={currency}
                  discount={discounts[id]}
                  additionalLoading={additionalLoadings[id]}
                  visible={selectedPlans.includes('ihhp_hospital_plan') || selectedPlans.includes('ihhp_non_hospitalisation_benefits')
                    || selectedPlans.includes('ihhp_medicine_appliances') || selectedPlans.includes('ihhp_dental_optical_a') || selectedPlans.includes('ihhp_dental_optical_b')
                  }
                  sx={{ width: 400, minWidth: 400 }}
                />
                <TableRatesRow
                  planId='ihhp_non_hospitalisation_benefits'
                  planName='non_hospitalisation_benefits'
                  planLabel={<span><span style={{ fontSize: 14 }}>Module 1</span> <span style={{ fontSize: 14 }}>(Non-Hospitalisation Benefits)</span></span>}
                  clients={clients}
                  selectedPlans={selectedPlans}
                  loading={loadingStatus[id]}
                  rates={response ? computeIHHP_Rates(response.rates) : []}
                  currency={currency}
                  discount={discounts[id]}
                  additionalLoading={additionalLoadings[id]}
                  visible={selectedPlans.includes('ihhp_non_hospitalisation_benefits')
                    || selectedPlans.includes('ihhp_medicine_appliances') || selectedPlans.includes('ihhp_dental_optical_a')
                    || selectedPlans.includes('ihhp_dental_optical_b')
                  }
                  sx={{ width: 400, minWidth: 400 }}
                />
                <TableRatesRow
                  planId='ihhp_medicine_appliances'
                  planName='medicine_appliances'
                  planLabel={<span><span style={{ fontSize: 14 }}>Module 2</span> <span style={{ fontSize: 14 }}>(Medicine & Appliances)</span></span>}
                  clients={clients}
                  selectedPlans={selectedPlans}
                  loading={loadingStatus[id]}
                  rates={response ? computeIHHP_Rates(response.rates) : []}
                  currency={currency}
                  discount={discounts[id]}
                  additionalLoading={additionalLoadings[id]}
                  visible={selectedPlans.includes('ihhp_medicine_appliances') || selectedPlans.includes('ihhp_dental_optical_a')
                    || selectedPlans.includes('ihhp_dental_optical_b')}
                  sx={{ width: 400, minWidth: 400 }}
                />
                <TableRatesRow
                  planId='ihhp_medical_evacuation_repatriation'
                  planName='medical_evacuation_repatriation'
                  planLabel={<span><span style={{ fontSize: 14 }}>Module 3</span> <span style={{ fontSize: 14 }}>(Medical Evacuation)</span></span>}
                  clients={clients}
                  selectedPlans={selectedPlans}
                  loading={loadingStatus[id]}
                  rates={response ? computeIHHP_Rates(response.rates) : []}
                  currency={currency}
                  discount={discounts[id]}
                  additionalLoading={additionalLoadings[id]}
                  visible={selectedPlans.includes('ihhp_medical_evacuation_repatriation')}
                  sx={{ width: 400, minWidth: 400 }}
                />
                <TableRatesRow
                  planId='ihhp_dental_optical_a'
                  planName='dental_optical_a'
                  planLabel={<span><span style={{ fontSize: 14 }}>Module 4A</span> <span style={{ fontSize: 14 }}>(Dental & Optical)</span></span>}
                  clients={clients}
                  selectedPlans={selectedPlans}
                  loading={loadingStatus[id]}
                  rates={response ? computeIHHP_Rates(response.rates) : []}
                  currency={currency}
                  discount={discounts[id]}
                  additionalLoading={additionalLoadings[id]}
                  visible={selectedPlans.includes('ihhp_dental_optical_a')}
                  sx={{ width: 400, minWidth: 400 }}
                />
                <TableRatesRow
                  planId='ihhp_dental_optical_b'
                  planName='dental_optical_b'
                  planLabel={<span><span style={{ fontSize: 14 }}>Module 4B</span> <span style={{ fontSize: 14 }}>(Enhanced Dental & Optical)</span></span>}
                  clients={clients}
                  selectedPlans={selectedPlans}
                  loading={loadingStatus[id]}
                  rates={response ? computeIHHP_Rates(response.rates) : []}
                  currency={currency}
                  discount={discounts[id]}
                  additionalLoading={additionalLoadings[id]}
                  visible={selectedPlans.includes('ihhp_dental_optical_b')}
                  sx={{ width: 400, minWidth: 400 }}
                />

                {
                  comparisons.length === 0 &&
                  <TableRow style={{ backgroundColor: '#ffffff' }}>
                    <TableCell style={{ border: '2px solid #fff', fontWeight: 600, background: '#bdbdbd' }}>{getPlanLabel()}</TableCell>
                    {
                      clients.map((client, idx) => {
                        return <TableCell style={{ border: '2px solid #fff', background: '#bdbdbd' }} key={idx}> {loadingStatus[id] ? <Skeleton variant="rounded" width={100} height={20} /> : formatRate(computeTotalPremiumPerClient(idx))}</TableCell>
                      })
                    }
                    <TableCell style={{ border: '2px solid #fff', background: '#bdbdbd' }}>{loadingStatus[id] ? <Skeleton variant="rounded" width={100} height={20} /> : formatRate(computeTotalPremiumPerPlan())}</TableCell>
                  </TableRow>
                }
                {
                  getSortedComparisons().map((comparison) => {
                    return (
                      <TableRow key={comparison.id} style={{ backgroundColor: '#ffffff' }}>
                        <TableCell style={{ border: '2px solid #fff', fontWeight: 600, background: '#bdbdbd' }}>{buildPlanLabel(comparison)}</TableCell>
                        {
                          clients.map((client, idx) => {
                            return <TableCell style={{ border: '2px solid #fff', background: '#bdbdbd' }} key={idx}> {loadingStatus[id] ? <Skeleton variant="rounded" width={100} height={20} /> : formatRate(computeTotalPremiumForPlansPerClient(comparison, idx))}</TableCell>
                          })
                        }
                        <TableCell style={{ border: '2px solid #fff', background: '#bdbdbd' }}>{loadingStatus[id] ? <Skeleton variant="rounded" width={100} height={20} /> : formatRate(computeTotalPremiumForPlansPerPlan(comparison))}</TableCell>
                      </TableRow>
                    );
                  })
                }

                {/* {
                  checkIfThereIsAnySelectedPlan() == false &&
                  <TableRow>
                    <TableCell>No Plan Selected</TableCell>
                  </TableRow>
                } */}
              </TableBody>
            </Table>

          </TableContainer>

          {
            isComparing &&
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1, mt: 1 }}>
              {
                getSortedComparisons().map((comparison) => (
                  <Chip
                    key={comparison.id}
                    size="small"
                    variant="outlined"
                    color="primary"
                    label={buildPlanLabel(comparison)}
                    onDelete={() => removeComparison(comparison.id)}
                    disabled={loadingStatus[id]}
                    sx={{ fontSize: 12 }}
                  />
                ))
              }
            </Box>
          }

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 1, mb: 2.5, mt: isComparing ? 0 : -1.5 }}>
            <Button
              size="small"
              variant="text"
              startIcon={<AddCircleIcon />}
              onClick={openComparisonDialog}
              disabled={loadingStatus[id]}
              sx={{ fontSize: 12, textTransform: 'none' }}
            >
              Compare
            </Button>
            {
              isComparing &&
              <Button
                size="small"
                variant="text"
                startIcon={<ClearAllIcon />}
                onClick={() => setComparisons([])}
                disabled={loadingStatus[id]}
                sx={{ fontSize: 12, textTransform: 'none' }}
              >
                Clear Comparison
              </Button>
            }
          </Box>

          <NotesDisplay appConfig={appConfig} insurerId={id} />

          <RatesVersionSelect
            insurer={id}
            label="Rates Version"
            insurerRates={insurerRates}
            selectedRatesVersion={selectedRatesVersion}
            setSelectedRatesVersion={setSelectedRatesVersion}
            disabled={loadingStatus[id]}
            handleFetchAppConfig={handleFetchAppConfig}
            onChange={() => setShowRefreshNow(true)}
          />
        </Box>

        <Dialog open={comparisonDialogOpen} onClose={() => setComparisonDialogOpen(false)} fullWidth maxWidth="xs">
          <DialogTitle>Add To Comparison</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography fontSize={15} fontWeight={600}>Plan Type</Typography>
              {
                modules.map((module) => (
                  <FormControlLabel
                    key={module.key}
                    sx={{ mb: 0, '& .MuiFormControlLabel-label': { fontSize: 13 } }}
                    control={
                      module.key === 'hp'
                        ? <Checkbox size="small" checked={comparisonSelection.hp} onChange={() => {}} inputProps={{ readOnly: true }} sx={{ cursor: 'default' }} />
                        : <Checkbox size="small" checked={!!comparisonSelection[module.key]} onChange={() => toggleComparisonSelection(module.key)} />
                    }
                    label={module.name}
                  />
                ))
              }
            </Box>
          </DialogContent>
          <DialogActions>
            <Button size="small" onClick={() => setComparisonDialogOpen(false)}>Cancel</Button>
            <Button size="small" variant="contained" onClick={addComparison}>Add</Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={showComparingLockedSnackbar}
          autoHideDuration={3000}
          onClose={() => setShowComparingLockedSnackbar(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          message="Clear the comparison to view other modules"
        />

      </Paper>

    </Box>
  );
});

export default IHHP_Rates;